// frontend/src/pages/Institution/InstitutionList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  Plus,
  Eye,
  Search,
  Filter,
  Building2,
  Download,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Image,
} from "lucide-react";
import { toast } from "react-toastify";
import "./InstitutionList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const InstitutionList = () => {
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [institutionTypes, setInstitutionTypes] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchInstitutions();
    fetchInstitutionTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterInstitutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutions, searchTerm, statusFilter, typeFilter]);

  const buildImageUrl = (filename) => {
    if (!filename) return null;

    if (filename.startsWith("http://") || filename.startsWith("https://")) {
      return filename;
    }

    if (filename.startsWith("/uploads")) {
      return url + filename;
    }

    return url + "/uploads/institutions/" + filename;
  };

  const fetchInstitutions = async () => {
    try {
      setLoading(true);

      const response = await axios.get(url + "/api/institution", {
        headers: getAuthHeaders(),
      });
      if (response.data.success) {
        const institutionsWithUrls = response.data.data.map((inst) => {
          const logoUrl = buildImageUrl(
            inst.institution_logo_url || inst.institution_logo
          );

          const sealUrl = buildImageUrl(
            inst.institution_seal_url || inst.institution_seal
          );

          const signatureUrl = buildImageUrl(
            inst.institution_signature_url || inst.institution_signature
          );
          return {
            ...inst,
            logoUrl: logoUrl,
            sealUrl: sealUrl,
            signatureUrl: signatureUrl,
          };
        });

        setInstitutions(institutionsWithUrls);

        const total = institutionsWithUrls.length;
        const active = institutionsWithUrls.filter(
          (i) => i.status === "active"
        ).length;
        const inactive = total - active;

        setStats({ total: total, active: active, inactive: inactive });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch institutions");
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutionTypes = async () => {
    try {
      const response = await axios.get(url + "/api/institution/types", {
        headers: getAuthHeaders(),
      });

      if (response.data.success) {
        setInstitutionTypes(response.data.data);
      }
    } catch (err) {
      // silent
    }
  };

  const filterInstitutions = () => {
    let filtered = institutions;

    if (searchTerm) {
      filtered = filtered.filter(
        (inst) =>
          inst.institution_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          inst.acronym?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inst.institution_code
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((inst) => inst.status === statusFilter);
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter(
        (inst) => inst.institution_type === typeFilter
      );
    }

    setFilteredInstitutions(filtered);
  };

  const handleDelete = async (id, name) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete "' +
        name +
        '"? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setDeleting(id);

      await axios.delete(url + "/api/institution/" + id, {
        headers: getAuthHeaders(),
      });

      toast.success('"' + name + '" deleted successfully');
      fetchInstitutions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete institution");
    } finally {
      setDeleting(null);
    }
  };

  const handleView = (id) => {
    navigate("/institution/" + id);
  };

  const handleEdit = (id) => {
    navigate("/institution/edit/" + id);
  };

  const handleAddInstitution = () => {
    navigate("/AddInstitution");
  };

  const handleExport = () => {
    toast.info("Export feature coming soon!");
  };

  const getStatusIcon = (status) => {
    if (status === "active") {
      return <CheckCircle size={14} className="status-icon active" />;
    }

    return <XCircle size={14} className="status-icon inactive" />;
  };

  const handleImageError = (e) => {
    e.currentTarget.style.display = "none";
  };

  const renderImageCell = (imageUrl, altText, emptyText) => {
    if (!imageUrl) {
      return (
        <div className="no-logo">
          <Image size={16} />
          <span>{emptyText}</span>
        </div>
      );
    }

    return (
      <div className="logo-container">
        <img
          src={imageUrl}
          alt={altText}
          className="institution-logo"
          onError={handleImageError}
        />
      </div>
    );
  };

  if (loading && institutions.length === 0) {
    return (
      <div className="currency-container">
        <div className="loading-container">
          <Loader2 className="animate-spin" size={32} />
          <p>Loading institutions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="currency-container">
      <div className="header">
        <div className="header-left">
          <Building2 size={24} color="white" />
          <div>
            <h2>Institutions Management</h2>
            <div className="info">
              <AlertCircle size={14} />
              Manage educational institutions, universities, and colleges
            </div>
          </div>
        </div>

        <button onClick={handleAddInstitution} className="add-button">
          <Plus size={16} /> Add Institution
        </button>
      </div>

      <div className="stats-bar">
        <span>
          Total Institutions: <strong>{stats.total}</strong>
        </span>
        <span>
          Active: <strong className="active-stat">{stats.active}</strong>
        </span>
        <span>
          Inactive: <strong className="inactive-stat">{stats.inactive}</strong>
        </span>
      </div>

      <div className="filters-container">
        <div className="search-wrapper">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search institutions by name, acronym, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input with-icon"
          />

          {searchTerm && (
            <button
              className="clear-search-btn"
              onClick={() => setSearchTerm("")}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="filter-group">
          <div className="filter-item">
            <Filter size={14} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="filter-item">
            <Filter size={14} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              {institutionTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <button className="export-btn" onClick={handleExport}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="currency-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Institution Name</th>
              <th>Type</th>
              <th>Acronym</th>
              <th>Manager</th>
              <th>Contact</th>
              <th>Logo</th>
              <th>Seal</th>
              <th>Signature</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredInstitutions.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  <div className="no-data-content">
                    <Building2 size={48} />
                    <p>
                      {searchTerm ||
                      statusFilter !== "all" ||
                      typeFilter !== "all"
                        ? "No institutions found matching your criteria"
                        : "No institutions found"}
                    </p>

                    {searchTerm && (
                      <button
                        className="clear-search"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear search
                      </button>
                    )}

                    {!searchTerm &&
                      statusFilter === "all" &&
                      typeFilter === "all" && (
                        <button
                          className="add-button"
                          onClick={handleAddInstitution}
                          style={{ marginTop: "15px" }}
                        >
                          <Plus size={16} className="mr-2" />
                          Add Your First Institution
                        </button>
                      )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredInstitutions.map((inst) => (
                <tr key={inst._id}>
                  <td>
                    <span className="institution-code">
                      {inst.institution_code}
                    </span>
                  </td>

                  <td>
                    <div className="institution-name">
                      <strong>{inst.institution_name}</strong>
                      <small>{inst.institution_location}</small>
                    </div>
                  </td>

                  <td>
                    <span className="type-badge">
                      {inst.institution_type}
                    </span>
                  </td>

                  <td>{inst.acronym || "-"}</td>
                  <td>{inst.current_manager || "-"}</td>
                  <td>{inst.contact || "-"}</td>

                  <td>
                    {renderImageCell(
                      inst.logoUrl,
                      inst.institution_name + " logo",
                      "No Logo"
                    )}
                  </td>

                  <td>
                    {renderImageCell(
                      inst.sealUrl,
                      inst.institution_name + " seal",
                      "No Seal"
                    )}
                  </td>

                  <td>
                    {renderImageCell(
                      inst.signatureUrl,
                      inst.institution_name + " signature",
                      "No Signature"
                    )}
                  </td>

                  <td>
                    <span className={"status-badge " + inst.status}>
                      {getStatusIcon(inst.status)}
                      {inst.status}
                    </span>
                  </td>

                  <td>
                    <div className="actions">
                      {/* <button
                        className="edit-btn"
                        onClick={() => handleView(inst._id)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button> */}

                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(inst._id)}
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>

                      <button
                        className="delete-btn"
                        onClick={() =>
                          handleDelete(inst._id, inst.institution_name)
                        }
                        disabled={deleting === inst._id}
                        title="Delete"
                      >
                        {deleting === inst._id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredInstitutions.length > 0 &&
        (searchTerm || statusFilter !== "all" || typeFilter !== "all") && (
          <div className="search-info">
            Showing {filteredInstitutions.length} of {institutions.length}{" "}
            institutions
            {(searchTerm || statusFilter !== "all" || typeFilter !== "all") &&
              " (filtered)"}
          </div>
        )}
    </div>
  );
};

export default InstitutionList;